import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Zap,
  BarChart3,
  Send,
  Globe,
  ChevronDown,
  CheckCircle2,
  Repeat,
  ShieldCheck,
  Server,
  TrendingUp,
  MessageSquare,
  RefreshCw,
  FileSpreadsheet,
  Wallet,
  Lock,
  QrCode,
  Link,
  LayoutTemplate,
} from "lucide-react";
import { useTranslation, useLocale } from "../contexts/LocaleContext";

const screenshots: Record<string, Record<string, string>> = {
  ru: {
    unlimited: "/features/ru-create.png",
    transfer: "/features/ru-list.png",
    analytics: "/features/ru-stats.png",
    repeating: "/features/ru-repeating.png",
    send: "/features/ru-email.png",
    multi: "/features/ru-create.png",
  },
  cs: {
    unlimited: "/features/cs-create.png",
    transfer: "/features/cs-list.png",
    analytics: "/features/cs-stats.png",
    repeating: "/features/cs-repeating.png",
    send: "/features/cs-email.png",
    multi: "/features/cs-create.png",
  },
  en: {
    unlimited: "/features/en-create.png",
    transfer: "/features/en-list.png",
    analytics: "/features/en-stats.png",
    repeating: "/features/en-repeating.png",
    send: "/features/en-email.png",
    multi: "/features/en-create.png",
  },
};

export default function FeatureSection() {
  const t = useTranslation();
  const { locale } = useLocale();
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved === "dark" || (!saved && window.matchMedia("(prefers-color-scheme: dark)").matches);
  });

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          setIsDarkMode(document.documentElement.classList.contains("dark"));
        }
      });
    });
    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  const features = [
    {
      id: "unlimited",
      text: t.landing.features.modern.unlimited,
      gradient: "from-[#ff4fd8] via-[#b86bff] to-[#5b3df5]",
      icon: <Zap className="w-5 h-5" />,
    },
    {
      id: "transfer",
      text: t.landing.features.grid.transfer,
      gradient: "from-[#3b82f6] via-[#2dd4bf] to-[#10b981]",
      icon: <RefreshCw className="w-5 h-5" />,
    },
    {
      id: "analytics",
      text: t.landing.features.modern.analytics,
      gradient: "from-[#10b981] via-[#059669] to-[#047857]",
      icon: <BarChart3 className="w-5 h-5" />,
    },
    {
      id: "repeating",
      text: t.landing.features.modern.repeating,
      gradient: "from-[#f59e0b] via-[#ea580c] to-[#dc2626]",
      icon: <Repeat className="w-5 h-5" />,
    },
    {
      id: "send",
      text: t.landing.features.modern.send,
      gradient: "from-[#f472ff] via-[#ec4899] to-[#8b5cf6]",
      icon: <Send className="w-5 h-5" />,
    },
    {
      id: "multi",
      text: t.landing.features.modern.multi,
      gradient: "from-[#3b82f6] via-[#2dd4bf] to-[#10b981]",
      icon: <Globe className="w-5 h-5" />,
    },
  ];

  const [active, setActive] = useState(features[0]);
  const [isPaused, setIsPaused] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const detailedFeatures = [
    // Row 1: Long
    {
      text: t.landing.features.grid.security,
      icon: <ShieldCheck className="w-5 h-5" />,
      gradient: "from-emerald-500 to-teal-400",
    },
    {
      text: t.landing.features.grid.messengers,
      icon: <MessageSquare className="w-5 h-5" />,
      gradient: "from-green-500 to-emerald-400",
    },
    {
      text: t.landing.features.grid.templates,
      icon: <LayoutTemplate className="w-5 h-5" />,
      gradient: "from-orange-500 to-yellow-400",
    },
    // Row 2: Short/Medium
    {
      text: t.landing.features.grid.eu_servers,
      icon: <Server className="w-5 h-5" />,
      gradient: "from-indigo-500 to-blue-400",
    },
    {
      text: t.landing.features.grid.expense_tracking,
      icon: <Wallet className="w-5 h-5" />,
      gradient: "from-violet-500 to-purple-400",
    },
    {
      text: t.landing.features.grid.password_links,
      icon: <Lock className="w-5 h-5" />,
      gradient: "from-slate-500 to-slate-400",
    },
    // Row 3: Medium
    {
      text: t.landing.features.grid.ecb_rates,
      icon: <Globe className="w-5 h-5" />,
      gradient: "from-amber-500 to-orange-400",
    },
    {
      text: t.landing.features.grid.excel_export,
      icon: <FileSpreadsheet className="w-5 h-5" />,
      gradient: "from-lime-500 to-green-400",
    },
    {
      text: t.landing.features.grid.transfer,
      icon: <RefreshCw className="w-5 h-5" />,
      gradient: "from-blue-500 to-cyan-400",
    },
    // Row 4: Short
    {
      text: t.landing.features.grid.charts,
      icon: <TrendingUp className="w-5 h-5" />,
      gradient: "from-rose-500 to-pink-400",
    },
    {
      text: t.landing.features.grid.qr_codes,
      icon: <QrCode className="w-5 h-5" />,
      gradient: "from-blue-600 to-indigo-500",
    },
    {
      text: t.landing.features.grid.website_link,
      icon: <Link className="w-5 h-5" />,
      gradient: "from-pink-500 to-rose-400",
    },
  ];

  const roadmapItems = [
    {
      title: t.landing.features.roadmap.mar,
      date: t.landing.features.roadmap.mar_date,
    },
    {
      title: t.landing.features.roadmap.apr,
      date: t.landing.features.roadmap.apr_date,
    },
  ];

  const rotate = useCallback(() => {
    setActive((current) => {
      const currentIndex = features.findIndex((f) => f.id === current.id);
      const nextIndex = (currentIndex + 1) % features.length;
      return features[nextIndex];
    });
  }, []);

  useEffect(() => {
    if (isPaused || isExpanded) return;

    const interval = setInterval(rotate, 5000);
    return () => clearInterval(interval);
  }, [isPaused, rotate, isExpanded]);

  return (
    <section className="relative w-full pt-4 pb-8 overflow-visible">
      <div className="mx-auto max-w-[1600px] px-6">
        {/* Title Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-8 lg:mb-10"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white tracking-tight text-center">
            {t.landing.features.title}
          </h2>
        </motion.div>

        <div className="relative grid grid-cols-1 gap-8 lg:gap-12 lg:grid-cols-[minmax(300px,1fr)_1.8fr] items-center">
          {/* LEFT – feature list */}
          <div
            className="flex flex-col gap-3 order-2 lg:order-1 relative z-10"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {features.map((f, i) => (
              <motion.div
                key={f.id}
                onMouseEnter={() => setActive(f)}
                onClick={() => setActive(f)}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                animate={{
                  x: active.id === f.id ? 10 : 0,
                }}
                transition={{
                  x: { type: "spring", stiffness: 500, damping: 30, mass: 1 },
                  opacity: { delay: i * 0.1 },
                }}
                viewport={{ once: true }}
                className={`group relative cursor-pointer rounded-[16px] border transition-all duration-500 overflow-hidden ${
                  active.id === f.id
                    ? "border-slate-300 dark:border-white/30 bg-slate-100/50 dark:bg-white/[0.12] shadow-lg dark:shadow-[0_10px_30px_rgba(0,0,0,0.4)]"
                    : "border-slate-200 dark:border-white/10 bg-slate-50/50 dark:bg-white/[0.05] hover:bg-slate-100/80 dark:hover:bg-white/[0.08]"
                }`}
              >
                {/* background gradient that is always visible */}
                <div
                  className={`absolute inset-0 -z-10 bg-gradient-to-br ${
                    f.gradient
                  } ${
                    active.id === f.id ? "opacity-10 dark:opacity-15" : "opacity-[0.03] dark:opacity-[0.05]"
                  } transition-opacity duration-500`}
                />

                {/* active glow */}
                <div
                  className={`absolute inset-0 -z-10 bg-gradient-to-br ${
                    f.gradient
                  } ${
                    active.id === f.id
                      ? "opacity-20 dark:opacity-30 blur-2xl"
                      : "opacity-0 blur-xl"
                  } transition-opacity duration-700`}
                />

                <div className="flex items-center gap-3 p-3 lg:p-4">
                  <div
                    className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center border transition-all duration-500 bg-gradient-to-br ${
                      f.gradient
                    } ${
                      active.id === f.id
                        ? "border-white/60 text-white shadow-lg shadow-white/20 scale-105"
                        : "border-white/20 text-white/80 scale-100"
                    }`}
                  >
                    {/* smaller icon */}
                    <div className="w-4.5 h-4.5 flex items-center justify-center">
                      {f.icon}
                    </div>
                  </div>

                  <h3 className="text-sm lg:text-base leading-tight font-semibold text-slate-900 dark:text-white drop-shadow-sm">
                    {f.text}
                  </h3>
                </div>
              </motion.div>
            ))}
          </div>

          {/* RIGHT – preview */}
          <div
            className="relative flex items-center justify-center lg:justify-start order-1 lg:order-2 w-full"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="relative w-full">
              {/* MacBook-like screen - SIGNIFICANTLY WIDER */}
              <motion.div
                layout
                className="relative aspect-[16/9] w-full rounded-xl bg-slate-100 dark:bg-[#08080a] shadow-2xl dark:shadow-[0_0_140px_rgba(232,121,249,0.38)] overflow-hidden flex flex-col border border-slate-200 dark:border-white/20"
              >
                {/* top bar */}
                <div className="flex h-5 lg:h-6 items-center px-4 border-b border-slate-200 dark:border-white/5 bg-slate-200/50 dark:bg-white/[0.03] flex-shrink-0">
                  <div className="flex gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-[#ff5f57]" />
                    <span className="h-2 w-2 rounded-full bg-[#febc2e]" />
                    <span className="h-2 w-2 rounded-full bg-[#28c840]" />
                  </div>
                </div>

                {/* screen content */}
                <div className="flex-1 relative overflow-hidden">
                  <AnimatePresence>
                    <motion.div
                      key={active.id + locale}
                      initial={{ opacity: 0, filter: "blur(10px)" }}
                      animate={{ opacity: 1, filter: "blur(0px)" }}
                      exit={{ opacity: 0, filter: "blur(10px)" }}
                      transition={{ duration: 0.6, ease: "easeInOut" }}
                      className="absolute inset-0 z-10"
                    >
                      <img
                        src={
                          screenshots[locale]?.[active.id] ||
                          screenshots["cs"][active.id]
                        }
                        alt={active.text}
                        className="w-full h-full object-cover object-top"
                      />
                    </motion.div>
                  </AnimatePresence>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* CTA and Expanded Features */}
        <div className="mt-8 lg:mt-12 flex flex-col items-center">
          <div className="flex flex-col md:flex-row items-center gap-6 lg:gap-8">
            <span className="text-lg lg:text-xl font-black text-slate-800 dark:text-white/90 drop-shadow-md">
              {t.landing.features.modern.notSure}
            </span>

            <motion.button
              onClick={() => setIsExpanded(!isExpanded)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="group relative flex items-center justify-center gap-2 py-3 px-6 rounded-2xl font-black text-base lg:text-lg shadow-xl transition-all duration-300 transform active:scale-95 border-0 bg-gradient-to-br from-purple-600 to-indigo-700 text-white hover:from-purple-500 hover:to-indigo-600"
            >
              <span>{t.landing.features.modern.fullList}</span>
              <motion.div
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.3 }}
                className="flex items-center"
              >
                <ChevronDown className="w-5 h-5" strokeWidth={3} />
              </motion.div>
            </motion.button>
          </div>

          <AnimatePresence>
            <motion.div
              initial={false}
              animate={{
                height: isExpanded ? "auto" : 0,
                opacity: isExpanded ? 1 : 0,
              }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className="w-full overflow-hidden"
              style={{ visibility: isExpanded ? "visible" : "hidden" }}
            >
              <div className="w-full max-w-7xl mt-8 px-4 lg:px-0 mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {detailedFeatures.map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={false}
                      animate={{
                        opacity: isExpanded ? 1 : 0,
                        scale: isExpanded ? 1 : 0.95,
                      }}
                      transition={{ delay: isExpanded ? idx * 0.05 : 0 }}
                      className="group relative rounded-[14px] border border-slate-200 dark:border-white/10 bg-white/50 dark:bg-white/[0.05] p-2.5 lg:p-3 backdrop-blur-xl transition-all duration-300 hover:bg-white/80 dark:hover:bg-white/[0.08] hover:border-slate-300 dark:hover:border-white/20 shadow-sm"
                    >
                      {/* background gradient */}
                      <div
                        className={`absolute inset-0 -z-10 bg-gradient-to-br ${item.gradient} opacity-[0.02] dark:opacity-[0.03] group-hover:opacity-[0.05] dark:group-hover:opacity-[0.08] transition-opacity duration-500 rounded-[14px]`}
                      />

                      <div className="flex items-center gap-2.5">
                        <div
                          className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center border border-white/10 bg-gradient-to-br ${item.gradient} text-white shadow-lg shadow-white/5 transition-transform duration-500 group-hover:scale-110`}
                        >
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            {item.icon}
                          </div>
                        </div>
                        <p className="text-[13px] leading-tight font-semibold text-slate-700 dark:text-white/90 drop-shadow-sm transition-colors group-hover:text-slate-900 dark:group-hover:text-white">
                          {item.text}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Roadmap Timeline Inside Expanded Section */}
                <div className="mt-12 w-full">
                  <div className="relative">
                    <h3 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white text-center mb-8">
                      {t.landing.features.modern.roadmapTitle}
                    </h3>

                    <div className="relative">
                      <div className="relative overflow-x-auto no-scrollbar md:overflow-visible pt-32 pb-12">
                        {/* Horizontal Line - Exactly centered relative to dots */}
                        <div className="absolute top-[138px] left-0 min-w-[1000px] md:w-full h-px bg-slate-200 dark:bg-white/10" />

                        <div className="relative flex justify-between items-center min-w-[1000px] w-full md:min-w-0">
                          {roadmapItems.map((item, idx) => (
                            <div
                              key={idx}
                              className="relative flex flex-col items-center px-4 flex-1"
                            >
                              {/* Label Above - Feature Description - BOLD & WHITE */}
                              <div className="absolute bottom-full mb-6 w-64 text-center">
                                <p className="text-[12px] lg:text-[14px] font-bold text-slate-900 dark:text-white leading-relaxed drop-shadow-md">
                                  {item.title}
                                </p>
                              </div>

                              {/* Dot - Exactly on the line */}
                              <div className="relative flex items-center justify-center h-2.5">
                                <div className="w-2.5 h-2.5 rounded-full bg-purple-600 dark:bg-purple-500 z-10 relative" />
                                <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-purple-600 dark:bg-purple-500 blur-sm animate-pulse" />
                              </div>

                              {/* Date Below - Month/Year - SMALL & DIMMED */}
                              <div className="absolute top-full mt-6 text-center">
                                <p className="text-[10px] lg:text-xs font-bold text-slate-400 dark:text-white/40 tracking-[0.2em] uppercase">
                                  {item.date}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Mobile Scroll Indicator */}
                      <div className="md:hidden flex justify-center mt-2">
                        <div className="flex gap-1.5 opacity-30">
                          {[...Array(3)].map((_, i) => (
                            <div
                              key={i}
                              className="w-1 h-1 rounded-full bg-slate-400 dark:bg-white animate-pulse"
                              style={{ animationDelay: `${i * 0.2}s` }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
